import {BaseVueEntity} from "@/entities/BaseVueEntity";
import {compareProperty, isDefined} from "@/utils";
import moment, { lang } from "moment";
import langHelper from "@/locales/langHelper";
import {Role} from "@/entities/Role";
import Stand from '@/entities/Stand';
import i18n from "@/locales/i18n";


const {t} = i18n.global;

const COLOR_MAP = new Map<string, boolean>()

export class Speech extends BaseVueEntity {


  private readonly _begin: string;
  private readonly _end: string;
  private readonly _info: string;
  private readonly _infoLang: string;
  private readonly _category: string;
  private readonly _place: string;
  private readonly _stand: string;
  private readonly _title: string;
  private readonly _sortOrder: string;
  private readonly _themes: string[];
  private readonly _hasTranslation: boolean;
  private readonly _subTitle: string;
  private readonly _sponsor: string;
  private readonly _searchList: string[];
  private readonly _logoPath: string;
  private readonly _persons: Person[]
  private readonly _logo: string;
  private readonly _stands: Stand[];
  private readonly _id: string;
  private readonly _exhibitorId: string;
  private readonly _exhibitorName: string;
  private readonly _color: string;
  private readonly _categoryColor: string;
  private readonly _parentId: string;
  private _subSpeeches: Speech[] = [];

  constructor(entity, path) {
    super();
    this._begin = entity.BEGINN;
    this._end = entity.ENDE;
    this._infoLang = entity.INFO_LANG;
    this._category = entity.KATEGORIE;
    this._place = entity.ORT;
    this._stand = entity.STAND;
    this._title = entity.TITEL;
    this._sortOrder = entity.S_ORDER;
    this._themes = entity.THEMEN;
    this._hasTranslation = entity.HAT_UEBERSETZUNG;
    this._subTitle = entity.UNTERTITEL;
    this._sponsor = entity.SPONSOR;
    this._logo = entity.LOGO;
    this._id = entity.ID;
    this._exhibitorId = entity.FIRMA_ID;
    this._exhibitorName = entity.FIRMA_NAME;
    this._color = entity.FARBE;
    this._parentId = entity.PARENT_ID;
    if (this._color) {
      this._categoryColor = this.isBrightColor(this._color) ? '#000' : '#FFF';
    }

    if (isDefined(entity.SUCHWORTLISTE)) {
      this._searchList = entity.SUCHWORTLISTE.split('|');
    }

    if (isDefined(entity.PERSONEN) && Array.isArray(entity.PERSONEN) && entity.PERSONEN.length > 0) {
      entity.PERSONEN = entity.PERSONEN.map(e => {
        return new Person(e)
      });
      this._persons = entity.PERSONEN;
    } else {
      this._persons = []
    }
    this._logoPath = path;

    if (isDefined(entity.INFO) && entity.INFO.includes('egcf-info-language')) {

      const pos = entity.INFO.indexOf('egcf-info-language');
      const end = entity.INFO.indexOf('</span', pos) + 7;
      const start = entity.INFO.lastIndexOf('<span', pos);
      entity.INFO = entity.INFO.slice(0, start) + entity.INFO.slice(end);
    }

    if (isDefined(entity.INFO)) {
      this._info = entity.INFO;
    }

    if (isDefined(entity.STAENDE)) {
      this._stands = entity.STAENDE.map(e => new Stand(e));
    }
  }


  static comparator(a: Speech, b: Speech): number {
    const diff = moment(a.begin).valueOf() - moment(b.begin).valueOf();
    if (diff === 0) {
      return compareProperty(a.sortOrder, b.sortOrder) || compareProperty(a.title, b.title);
    }
    return diff;
  }


  private isBrightColor(hexColor: string): boolean {
    const cachedValue = COLOR_MAP.get(hexColor);
    if (cachedValue !== undefined) {
      return cachedValue;
    }

    const rgbValues = this.hexToRGB(hexColor);

    const red = rgbValues[0] / 255;
    const green = rgbValues[1] / 255;
    const blue = rgbValues[2] / 255;

    const brightness = (0.2126 * Math.pow(red, 2.2) + 0.7152 * Math.pow(green, 2.2) + 0.0722 * Math.pow(blue, 2.2)) * 255.0;
    const isBrightColor = brightness > 130;
    COLOR_MAP.set(hexColor, isBrightColor);
    return isBrightColor;
  }

  private hexToRGB(hexColor: string): number[] {
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4), 16);
    return [r, g, b];
  }

  public showInfoInsteadOfSpeakers(): boolean {
    return this.info && this.info.includes('egcf-info-multipanel-header');
  }

  public hasSearchValue(value: string): boolean {
    for (const prop of ['_title', '_subTitle', '_info', '_place', '_infoLang', '_sponsor', '_searchList']) {
      if (isDefined(this[prop]) && Array.isArray(this[prop])) {
        return this[prop].findIndex(e => e.toLowerCase().indexOf(value) > -1) > -1;
      }
      if (isDefined(this[prop]) && this[prop].toLowerCase().indexOf(value) > -1) {
        return true;
      }
    }
    return false;
  }

  get beginTime(): string {
    return moment(this._begin).locale(langHelper.getCurrentLang()).format("HH:mm");
  }

  get endTime(): string {
    return moment(this._end).locale(langHelper.getCurrentLang()).format("HH:mm");
  }

  get isAllDay(): boolean {
    let s = this.beginTime + ' - ' + this.endTime;
    return s === '00:00 - 23:59' || s === '00:00 - 00:00';
  }

  get timeSpanPrefix(): string {
    if (this.isAllDay) {
      return t('all-day');
    }
    return this.beginTime;
  }

  get timeSpan(): string {
    if (this.isAllDay) {
      return t('all-day');
    } else if (this.beginTime === this.endTime) {
      return t('time-from') + ' ' + this.beginTime;
    } else {
      return this.beginTime + ' - ' + this.endTime;
    }
  }

  get startDate(): string {
    const lang = langHelper.getCurrentLang();
    if (lang == 'de') {
      return moment(this._begin).locale(langHelper.getCurrentLang()).format("Do MMMM YYYY");
    } else {
      return moment(this._begin).locale(langHelper.getCurrentLang()).format("D MMMM YYYY");
    }
  }

  get begin(): string {
    return this._begin;
  }

  get end(): string {
    return this._end;
  }

  get info(): string {
    return this._info;
  }

  get category(): string {
    return this._category;
  }

  get place(): string {
    return this._place;
  }

  get stand(): string {
    return this._stand;
  }

  get title(): string {
    return this._title;
  }

  get sortOrder(): string {
    return this._sortOrder;
  }

  get logoPath(): string {
    return this._logoPath;
  }


  get themes(): string[] {
    return this._themes;
  }

  get languageID(): string {
    if (isDefined(this.themes)) {
      return this.themes.find(t => t.includes('EVENTS_BY_LANGUAGE'));
    }
    return undefined;
  }

  get hasTranslation(): boolean {
    return this._hasTranslation;
  }

  get persons(): Person[] {
    return this._persons;
  }


  get logo(): string {
    return this._logo;
  }


  get id(): string {
    return this._id;
  }


  get stands(): Stand[] {
    return this._stands ?? [];
  }

  getLogoUrl(): string {
    return `${process.env.VUE_APP_BACKEND_URL}/${this._logoPath}${this._logo}`
  }

  getPersonsByRole(role: Role): Person[] {
    return this.persons.filter(p => p.role === role.id);
  }

  hasSpeakerWithRole(role: Role): boolean {
    return this.persons.findIndex(p => p.role === role.id) > -1;
  }


  get exhibitorId(): string {
    return this._exhibitorId;
  }

  get exhibitorName(): string {
    return this._exhibitorName;
  }

  get color(): string {
    return this._color;
  }


  get categoryColor(): string {
    return this._categoryColor;
  }

  get getIconStyle(): string {
    //return isDefined(this._color) && this._color.length === 6 ? (`color: #${this._color};`) : ''
    return "color: var(--primary-color);";
  }


  get parentId(): string {
    return this._parentId;
  }


  get subSpeeches(): Speech[] {
    return this._subSpeeches;
  }

  set subSpeeches(value: Speech[]) {
    this._subSpeeches = value;
  }
}


export class Person {
  private readonly _id: string;
  private readonly _role: string;
  private readonly _sortOrder: string;

  constructor(entity) {
    this._id = entity.ID;
    this._role = entity.ROLLE;
    this._sortOrder = entity.S_ORDER;
  }


  get id(): string {
    return this._id;
  }

  get role(): string {
    return this._role;
  }

  get sortOrder(): string {
    return this._sortOrder;
  }
}
